/**
 * Network / Systems domain connector manifests.
 *
 * Four file-based connectors for cloud infrastructure imports, each driven
 * by the shared `importCloudResources` parser via CloudInventoryAdapter:
 *   - aws-live-scan      (ScanResult bundle from infra-generate-aws skill)
 *   - terraform-state    (terraform.tfstate v4+)
 *   - cloudformation     (CFN JSON template)
 *   - aws-config-snapshot (AWS Config snapshot JSON)
 *   - scanner-report     (SARIF, Prowler, ScoutSuite, Heimdall/HDF)
 */
import { registerConnector } from '../../../core/data-profiles/connectorRegistry';
import { AWS_HOSTED_ACCOUNT_CONNECTOR_ID } from '../models/AwsHostedAccountSource';

type RegisterArgs = Parameters<typeof registerConnector>[0];

export function registerNetworkConnectors(): void {
  safeRegister({
    id: AWS_HOSTED_ACCOUNT_CONNECTOR_ID,
    label: 'AWS account',
    description: 'Hosted IAM role connection for scheduled cloud architecture review sync.',
    iconRef: 'assets/connectors/aws.svg',
    format: 'api-key',
    inputHint: 'IAM role ARN with external ID trust',
    folderKinds: ['network'],
    diagramTypes: ['network', 'architecture'],
    targets: ['folder'],
    compatibleProfileIds: ['network'],
    source: {
      system: 'aws',
      defaultIntent: 'system_inventory',
      emitsDeltas: true,
      retentionClass: 'rolling_window',
      volume: 'high',
    },
    status: 'beta',
    adapter: () =>
      import('../services/adapters/AwsHostedAccountAdapter').then(
        (m) => new m.AwsHostedAccountAdapter() as never,
      ),
  });

  safeRegister({
    id: 'aws-live-scan',
    label: 'AWS live scan',
    description: 'Bundle emitted by the infra-generate-aws skill (ScanResult JSON).',
    iconRef: 'assets/connectors/aws.svg',
    format: 'file',
    inputHint: 'scan.json from the infra-generate-aws skill',
    folderKinds: ['network'],
    diagramTypes: ['network', 'architecture'],
    targets: ['diagram', 'folder', 'both'],
    compatibleProfileIds: ['network'],
    source: {
      system: 'aws',
      defaultIntent: 'system_inventory',
      emitsDeltas: true,
      retentionClass: 'rolling_window',
      volume: 'high',
    },
    status: 'stable',
    adapter: () =>
      import('../services/adapters/CloudInventoryAdapter').then(
        (m) => new m.CloudInventoryAdapter('aws-scan') as never,
      ),
  });

  safeRegister({
    id: 'terraform-state',
    label: 'Terraform state',
    description: 'terraform.tfstate (v4+) — dependencies and attrs become edges.',
    iconRef: 'assets/connectors/terraform.svg',
    format: 'file',
    inputHint: '.tfstate or JSON file',
    folderKinds: ['network'],
    diagramTypes: ['network', 'architecture'],
    targets: ['diagram', 'folder', 'both'],
    compatibleProfileIds: ['network'],
    source: {
      system: 'terraform',
      defaultIntent: 'system_inventory',
      retentionClass: 'audit_retained',
      volume: 'high',
    },
    status: 'stable',
    adapter: () =>
      import('../services/adapters/CloudInventoryAdapter').then(
        (m) => new m.CloudInventoryAdapter('terraform-state') as never,
      ),
  });

  safeRegister({
    id: 'cloudformation',
    label: 'CloudFormation',
    description: 'JSON template — Ref and Fn::GetAtt intrinsics become edges.',
    iconRef: 'assets/connectors/cfn.svg',
    format: 'file',
    inputHint: 'CFN JSON template (YAML not yet supported)',
    folderKinds: ['network'],
    diagramTypes: ['network', 'architecture'],
    targets: ['diagram', 'folder', 'both'],
    compatibleProfileIds: ['network'],
    source: {
      system: 'cloudformation',
      defaultIntent: 'system_inventory',
      retentionClass: 'audit_retained',
      volume: 'medium',
    },
    status: 'stable',
    adapter: () =>
      import('../services/adapters/CloudInventoryAdapter').then(
        (m) => new m.CloudInventoryAdapter('cloudformation') as never,
      ),
  });

  safeRegister({
    id: 'aws-config-snapshot',
    label: 'AWS Config snapshot',
    description: 'aws configservice get-resource-config-history / Config snapshot JSON.',
    iconRef: 'assets/connectors/aws-config.svg',
    format: 'file',
    inputHint: 'JSON with configurationItems[]',
    folderKinds: ['network'],
    diagramTypes: ['network', 'architecture'],
    targets: ['diagram', 'folder', 'both'],
    compatibleProfileIds: ['network'],
    source: {
      system: 'aws_config',
      defaultIntent: 'system_inventory',
      emitsDeltas: true,
      retentionClass: 'rolling_window',
      volume: 'high',
    },
    status: 'stable',
    adapter: () =>
      import('../services/adapters/CloudInventoryAdapter').then(
        (m) => new m.CloudInventoryAdapter('aws-config') as never,
      ),
  });

  safeRegister({
    id: 'scanner-report',
    label: 'Scanner report',
    description: 'SARIF, Prowler, ScoutSuite, or Heimdall/HDF findings as GRC evidence.',
    iconRef: 'assets/connectors/security-scan.svg',
    format: 'file',
    inputHint: 'SARIF, Prowler JSON, ScoutSuite JSON, or Heimdall HDF JSON',
    folderKinds: ['network'],
    diagramTypes: ['network', 'architecture'],
    targets: ['folder'],
    compatibleProfileIds: ['grc-control'],
    source: {
      system: 'scanner_report',
      defaultIntent: 'control_risk_register',
      retentionClass: 'audit_retained',
      volume: 'high',
    },
    status: 'beta',
    adapter: () =>
      import('../services/adapters/ScannerReportAdapter').then(
        (m) => new m.ScannerReportAdapter() as never,
      ),
  });
}

function safeRegister(manifest: RegisterArgs): void {
  try {
    registerConnector(manifest);
  } catch (err) {
    if (err instanceof Error && err.message.includes('duplicate connector id')) return;
    throw err;
  }
}
